import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeader from "@/components/feature/SectionHeader";
import { aboutBanner, siteInfo } from "@/mocks/manga";

const milestones = [
  {
    year: "2020",
    title: "扬帆起航",
    desc: "43423漫画网正式上线，首批上线300余部免费漫画作品，开启“免费漫画阅读”之路。",
    icon: "ri-rocket-2-line",
  },
  {
    year: "2022",
    title: "口碑积累",
    desc: "全站作品突破5000部，注册读者突破100万，持续蝉联免费漫画平台好评榜前列。",
    icon: "ri-medal-line",
  },
  {
    year: "2024",
    title: "体验升级",
    desc: "完成高清图源升级与阅读器重构，上线《健身教练》等旗舰作品，阅读体验大幅提升。",
    icon: "ri-refresh-line",
  },
  {
    year: "2026",
    title: "初心不改",
    desc: "全站每日更新超200话，累计服务读者超千万，始终坚持免费、高清、好体验。",
    icon: "ri-heart-2-line",
  },
];

const testimonials = [
  {
    name: "熬夜追更的喵",
    role: "《健身教练》铁杆读者",
    icon: "ri-user-star-line",
    text: "《健身教练》真的太上头了，从第1话一路追到完结，还顺带学到了不少健身干货，墙裂推荐！",
  },
  {
    name: "健身小达人",
    role: "都市体育漫画爱好者",
    icon: "ri-fire-line",
    text: "画风干净又热血，主角蜕变的过程看得我热血沸腾，看完直接去办了张健身卡。",
  },
  {
    name: "住在地球村",
    role: "每日打卡读者",
    icon: "ri-global-line",
    text: "更新非常准时，页面没有乱七八糟的广告弹窗，阅读体验真的舒服，五星好评！",
  },
  {
    name: "打工人小许",
    role: "通勤追漫读者",
    icon: "ri-briefcase-line",
    text: "通勤路上看几话完全不卡顿，免费还能这么高清，这样的良心网站不多见了。",
  },
];

const stats = [
  { value: "5000+", label: "全站漫画作品" },
  { value: "1000万+", label: "累计服务读者" },
  { value: "200+", label: "每日更新话数" },
  { value: "98%", label: "读者好评率" },
];

const contactItems = [
  { icon: "ri-mail-line", label: "邮箱", value: siteInfo.email, desc: "商务合作 / 版权相关" },
  { icon: "ri-phone-line", label: "客服电话", value: siteInfo.phone, desc: "周一至周日 9:00-21:00" },
  { icon: "ri-map-pin-2-line", label: "办公地址", value: siteInfo.address, desc: "欢迎来信来访" },
  { icon: "ri-time-line", label: "工作时间", value: siteInfo.workHours, desc: "法定节假日除外" },
];

export default function AboutPage() {
  const jsonLd = [
    {
      "@type": "AboutPage",
      url: "https://workoutpro0.github.io/about",
      name: "关于43423漫画网",
      inLanguage: "zh-CN",
      isPartOf: { "@id": "https://workoutpro0.github.io/#website" },
      about: {
        "@type": "Organization",
        name: siteInfo.name,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        address: { "@type": "PostalAddress", streetAddress: siteInfo.address, addressCountry: "CN" },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://workoutpro0.github.io/" },
        { "@type": "ListItem", position: 2, name: "关于我们" },
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title="关于我们-43423漫画网：品牌介绍、口碑评价与联系方式"
        description="了解43423漫画网：坚持免费漫画阅读的在线平台，介绍品牌故事、发展历程、读者口碑与联系方式，随时与我们联系。"
        path="/about"
        jsonLd={jsonLd}
      />

      <section className="relative flex min-h-[280px] w-full items-center justify-center overflow-hidden md:min-h-[360px]">
        <img
          src={aboutBanner}
          alt="关于43423漫画网品牌横幅"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/45 to-background-950/70"></div>
        <div className="relative z-10 mx-auto w-full max-w-3xl px-4 py-14 text-center md:px-6">
          <span className="anim-fade-up inline-flex items-center gap-1.5 rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 text-xs font-bold text-background-50 backdrop-blur-sm">
            <i className="ri-community-line text-accent-500"></i>
            关于我们
          </span>
          <h1 className="anim-fade-up mt-4 font-heading text-3xl font-black leading-snug text-background-50 md:text-4xl">
            让每一部好漫画，都被免费看见
          </h1>
          <p className="anim-fade-up mx-auto mt-4 max-w-xl text-sm leading-relaxed text-background-50/85 md:text-base">
            {siteInfo.name}成立于2020年，是一家专注免费漫画阅读的在线平台。我们相信好的作品不该被付费挡在门外，坚持用品质与温度服务每一位读者。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <section className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4 md:gap-4">
          {stats.map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-background-200/70 bg-background-100 p-5 text-center transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300/60"
            >
              <p className="font-comic text-2xl font-bold text-primary-600 md:text-3xl">{item.value}</p>
              <p className="mt-1.5 text-xs text-foreground-600">{item.label}</p>
            </div>
          ))}
        </section>

        <section className="mt-12" aria-label="品牌介绍">
          <SectionHeader
            title="品牌介绍"
            subtitle="一个关于热爱与坚持的故事"
          />
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-10">
            <div className="w-full lg:w-1/2">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <img
                  src={aboutBanner}
                  alt="43423漫画网品牌介绍配图"
                  title="43423漫画网品牌介绍"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <h3 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                免费，是我们最大的诚意
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                {siteInfo.name}诞生于2020年，创始团队是一群狂热的漫画爱好者。我们见过太多好作品因为付费门槛而无人问津，也见过太多读者因为高昂的订阅费而放弃热爱。于是我们决定：做一家真正免费的漫画阅读平台。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                六年走来，我们始终坚持三大原则：<strong className="font-bold text-primary-600">全部免费</strong>——全站作品无需付费；<strong className="font-bold text-primary-600">高清画质</strong>——精选图源呈现每一格细节；<strong className="font-bold text-primary-600">每日更新</strong>——连载作品第一时间上架。旗舰作品《健身教练》漫画全集，更是一经上线便成为无数读者的心头好。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                未来，我们会继续扩充作品库、打磨阅读体验，让更多人在这里找回阅读的快乐。
              </p>
            </div>
          </div>
        </section>

        <section className="mt-12 bg-background-100 px-4 py-10 md:px-6 md:py-14">
          <SectionHeader
            title="发展历程"
            subtitle="一步一个脚印，与千万读者共同成长"
            align="center"
          />
          <div className="relative mx-auto max-w-3xl">
            <span className="absolute left-[19px] top-2 bottom-2 w-px bg-background-300 md:left-1/2"></span>
            <div className="flex flex-col gap-8">
              {milestones.map((item, index) => (
                <div
                  key={item.year}
                  className={`relative flex flex-col gap-3 pl-12 md:w-1/2 md:pl-0 ${
                    index % 2 === 0 ? "md:pr-10 md:text-right" : "md:ml-auto md:pl-10"
                  }`}
                >
                  <span
                    className={`absolute left-0 top-1 flex h-10 w-10 items-center justify-center rounded-full border-2 border-background-50 bg-accent-500 text-base text-background-50 ${
                      index % 2 === 0 ? "md:left-auto md:-right-5" : "md:-left-5"
                    }`}
                  >
                    <i className={item.icon}></i>
                  </span>
                  <div>
                    <p className="font-comic text-lg font-bold text-primary-600">{item.year}</p>
                    <h3 className="mt-1 text-sm font-black text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-12" aria-label="读者口碑">
          <SectionHeader
            title="读者口碑"
            subtitle="来自千万读者的真实评价，是我们坚持的最大动力"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 md:gap-4">
            {testimonials.map((item) => (
              <div
                key={item.name}
                className="flex flex-col rounded-lg border border-background-200/70 bg-background-100 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300/60"
              >
                <span className="flex items-center gap-0.5 text-accent-500" aria-label="五星好评">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-sm"></i>
                  ))}
                </span>
                <p className="mt-3 flex-1 text-xs leading-relaxed text-foreground-700">“{item.text}”</p>
                <div className="mt-4 flex items-center gap-2.5 border-t border-background-200/60 pt-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-50 text-sm text-primary-600">
                    <i className={item.icon}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold text-foreground-950">{item.name}</p>
                    <p className="truncate text-[11px] text-foreground-600">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12" aria-label="联系方式">
          <SectionHeader
            title="联系我们"
            subtitle="有任何问题或合作意向，欢迎随时与我们联系"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 md:gap-4">
            {contactItems.map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-background-200/70 bg-background-100 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300/60"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-lg text-primary-600">
                  <i className={item.icon}></i>
                </span>
                <p className="mt-3 text-[11px] text-foreground-600">{item.label}</p>
                <p className="mt-0.5 break-all text-xs font-bold text-foreground-950 md:text-sm">{item.value}</p>
                <p className="mt-1 text-[11px] text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <div className="flex items-center gap-2 border-b border-background-200 bg-background-100 px-4 py-3">
              <i className="ri-map-pin-2-line text-primary-500"></i>
              <p className="text-xs font-bold text-foreground-950">公司位置 · {siteInfo.address}</p>
            </div>
            <iframe
              title="43423漫画网办公地址地图"
              src="https://www.google.com/maps?q=Shanghai%20Zhangjiang%20Hi-Tech%20Park&output=embed"
              className="h-[300px] w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            ></iframe>
          </div>
        </section>
      </div>
    </>
  );
}